import { useCallback, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import axios, { AxiosError, AxiosResponse, isAxiosError } from 'axios'
import { toast, ToastT } from 'sonner'

import { persister } from '~/app/_root/QueryClientProvider'
import { QUERY_KEYS } from '~/constants/react-query'
import { getAuthCookie, useCookieStore } from '~/store/cookieStore'
import { captureException } from '~/utils/errors'
import {
  getAxiosConfig,
  clearBatcherCache,
  getPathWithParams,
} from '~/utils/requests'

export type ServerRequestArgs = {
  path: string
  headers?: { [key: string]: string }
  errorToastId?: string
  processingMessage?: string
  successMessage?: string
  successMessageOptions?: Omit<ToastT, 'id'> & { id?: string }
  errorMessage: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrideErrorMessageOptions?: (e: any) => undefined | Omit<ToastT, 'id'>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (e: any) => void
}

type ServerPostArgs = ServerRequestArgs & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: { [key: string]: any }
}

type ServerPutArgs = ServerRequestArgs & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: { [key: string]: any }
}

export type ServerGetArgs = Omit<ServerRequestArgs, 'errorMessage'> & {
  params?: { [key: string]: string | boolean | number }
  errorMessage?: string
  responseType?: 'json' | 'blob'
  signal?: AbortSignal
}

type ServerDeleteArgs = ServerRequestArgs

const DEFAULT_SERVER_ERROR_MESSAGE = 'Sorry, something went wrong!'
const DEFAULT_ERROR_TOAST_DURATION = 4000

export const useServerRequests = (): {
  serverPostRequest: <ServerResponseType>(
    args: ServerPostArgs,
  ) => Promise<AxiosResponse<ServerResponseType> | void>
  serverPutRequest: <ServerResponseType>(
    arg: ServerPutArgs,
  ) => Promise<AxiosResponse<ServerResponseType> | void>
  serverGetRequest: <ServerResponseType>(
    args: ServerGetArgs,
  ) => Promise<AxiosResponse<ServerResponseType> | void>
  serverDeleteRequest: <ServerResponseType>(
    args: ServerDeleteArgs,
  ) => Promise<AxiosResponse<ServerResponseType> | void>
  isProcessing: boolean
} => {
  const authToken = useCookieStore((s) => s.authToken)
  const [isProcessing, setIsProcessing] = useState(false)

  const config = useMemo(() => {
    const axiosConfig = getAxiosConfig()
    return {
      ...axiosConfig,
      headers: {
        ...(axiosConfig.headers ?? {}),
        ...(authToken ? { Authorization: authToken } : {}),
      },
    }
  }, [authToken])

  const expireSession = useExpireSession()

  const errorHandler = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      e: any,
      options: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError?: (e: any) => void
        clientErrorMessage?: string
        errorToastId?: string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        overrideErrorMessageOptions?: (e: any) => undefined | Omit<ToastT, 'id'>
      },
    ) => {
      const { onError, clientErrorMessage, errorToastId } = options

      const statusCode = e.response?.status ?? 500

      if (statusCode === 401) {
        const rejectedAuthToken = e.config?.headers?.Authorization
        const cookieAuthToken = getAuthCookie()

        if (cookieAuthToken && cookieAuthToken !== rejectedAuthToken) {
          useCookieStore.getState().setAuthToken(cookieAuthToken)
        } else {
          expireSession()
        }
      }

      onError?.(e)

      if (
        statusCode === 428 &&
        getSplitsErrorCodeString(e) === 'PASSKEY_CONFIRMATION_REQUIRED'
      ) {
        toast.error('Passkey confirmation required, try again', {
          duration: DEFAULT_ERROR_TOAST_DURATION,
          id: errorToastId,
        })
        return
      }

      // Toasts and Sentry should only fire for user-initiated actions. Treat
      // GETs as background fetches and skip both — callers that do want a
      // toast on a GET can opt in by passing `errorMessage`. We also silence
      // GETs in Sentry to avoid spam from polling retries on missing resources.
      const isBackgroundFetch = e.config?.method?.toLowerCase() === 'get'

      // Don't report transient network errors (e.g. ERR_NETWORK) to Sentry —
      // these are browser-level XHR failures with no HTTP response and are
      // typically caused by temporary connectivity issues or browser memory
      // pressure, not actionable bugs.
      if (
        !isBackgroundFetch &&
        (!isAxiosError(e) || e.code !== AxiosError.ERR_NETWORK)
      ) {
        captureException(e)
      }

      const serverErrorMessage: string | undefined =
        getSplitsErrorMessage(e) !== DEFAULT_SERVER_ERROR_MESSAGE
          ? getSplitsErrorMessage(e)
          : undefined

      if (clientErrorMessage) {
        toast.error(clientErrorMessage, {
          description: serverErrorMessage,
          duration: DEFAULT_ERROR_TOAST_DURATION,
          id: errorToastId,
          ...(options.overrideErrorMessageOptions?.(e) ?? {}),
        })
      } else if (serverErrorMessage && !isBackgroundFetch) {
        toast.error(serverErrorMessage, {
          duration: DEFAULT_ERROR_TOAST_DURATION,
          id: errorToastId,
          ...(options.overrideErrorMessageOptions?.(e) ?? {}),
        })
      }
    },
    [expireSession],
  )

  const serverPostRequest = useCallback(
    async <ServerResponseType>(args: ServerPostArgs) => {
      let toastId

      try {
        setIsProcessing(true)
        if (args.processingMessage) {
          toastId = toast.loading(args.processingMessage)
        }
        const result = await axios.post<ServerResponseType>(
          args.path,
          args.params,
          {
            ...config,
            headers: {
              ...config.headers,
              ...args.headers,
            },
          },
        )

        if (args.successMessage) {
          toast.success(args.successMessage, {
            id: toastId,
            duration: 4000,
            ...(args.successMessageOptions ?? {}),
          })
        } else if (toastId) {
          toast.dismiss(toastId)
        }

        return result
      } catch (e) {
        if (toastId) toast.dismiss(toastId)
        errorHandler(e, {
          onError: args.onError,
          clientErrorMessage: args.errorMessage,
          errorToastId: args.errorToastId,
          overrideErrorMessageOptions: args.overrideErrorMessageOptions,
        })
      } finally {
        setIsProcessing(false)
      }
    },
    [config, errorHandler],
  )

  const serverPutRequest = useCallback(
    async <ServerResponseType>(args: ServerPutArgs) => {
      let toastId

      try {
        setIsProcessing(true)
        if (args.processingMessage) {
          toastId = toast.loading(args.processingMessage)
        }
        const result = await axios.put<ServerResponseType>(
          args.path,
          args.params,
          {
            ...config,
            headers: {
              ...config.headers,
              ...args.headers,
            },
          },
        )

        if (args.successMessage) {
          toast.success(args.successMessage, {
            id: toastId,
            duration: 4000,
            ...(args.successMessageOptions ?? {}),
          })
        } else if (toastId) {
          toast.dismiss(toastId)
        }

        return result
      } catch (e) {
        if (toastId) toast.dismiss(toastId)
        errorHandler(e, {
          onError: args.onError,
          clientErrorMessage: args.errorMessage,
          errorToastId: args.errorToastId,
          overrideErrorMessageOptions: args.overrideErrorMessageOptions,
        })
      } finally {
        setIsProcessing(false)
      }
    },
    [config, errorHandler],
  )

  const serverGetRequest = useCallback(
    async <ServerResponseType>(args: ServerGetArgs) => {
      const pathWithParams = getPathWithParams(args.path, args.params ?? {})
      try {
        setIsProcessing(true)
        const result = await axios.get<ServerResponseType>(pathWithParams, {
          ...config,
          ...(args.responseType ? { responseType: args.responseType } : {}),
          ...(args.signal ? { signal: args.signal } : {}),
        })
        return result
      } catch (e) {
        errorHandler(e, {
          onError: args.onError,
          clientErrorMessage: args.errorMessage,
          errorToastId: args.errorToastId,
          overrideErrorMessageOptions: args.overrideErrorMessageOptions,
        })
      } finally {
        setIsProcessing(false)
      }
    },
    [config, errorHandler],
  )

  const serverDeleteRequest = useCallback(
    async <ServerResponseType>(args: ServerDeleteArgs) => {
      let toastId

      try {
        setIsProcessing(true)
        if (args.processingMessage) {
          toastId = toast.loading(args.processingMessage)
        }
        const result = await axios.delete<ServerResponseType>(args.path, {
          ...config,
          headers: {
            ...config.headers,
            ...args.headers,
          },
        })

        if (args.successMessage) {
          toast.success(args.successMessage, {
            id: toastId,
            duration: 4000,
            ...(args.successMessageOptions ?? {}),
          })
        } else if (toastId) {
          toast.dismiss(toastId)
        }

        return result
      } catch (e) {
        if (toastId) toast.dismiss(toastId)
        errorHandler(e, {
          onError: args.onError,
          clientErrorMessage: args.errorMessage,
          errorToastId: args.errorToastId,
          overrideErrorMessageOptions: args.overrideErrorMessageOptions,
        })
      } finally {
        setIsProcessing(false)
      }
    },
    [config, errorHandler],
  )

  return {
    serverPostRequest,
    serverPutRequest,
    serverGetRequest,
    serverDeleteRequest,
    isProcessing,
  }
}

export type ServerGetRequestFunction = ReturnType<
  typeof useServerRequests
>['serverGetRequest']

export const useClearAuth = (): (() => void) => {
  const removeAuthToken = useCookieStore((s) => s.removeAuthToken)
  const queryClient = useQueryClient()

  const clearAccount = useCallback(() => {
    removeAuthToken()
    queryClient.clear()
    persister.removeClient()
    clearBatcherCache()
  }, [removeAuthToken, queryClient])

  return clearAccount
}

// Teardown for a 401 outside of an explicit sign-out. Must NOT call
// queryClient.clear(): evicting a mounted query makes it refetch, which 401s and
// clears again — an unbounded loop, since each pass is a fetch, not a retry.
const useExpireSession = (): (() => void) => {
  const removeAuthToken = useCookieStore((s) => s.removeAuthToken)
  const queryClient = useQueryClient()

  return useCallback(() => {
    removeAuthToken()
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.AUTH_USER] })
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.ORG_CONTACTS],
      refetchType: 'none',
    })
    persister.removeClient()
    clearBatcherCache()
  }, [removeAuthToken, queryClient])
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSplitsErrorCodeString = (e: any) => {
  return e.response?.data?.splitsErrorCodeString
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getSplitsErrorMessage = (e: any) => {
  return e.response?.data?.message
}
